import React, { ReactNode, useRef, useState } from 'react';
import {
  Image as ImageIcon,
  Plus,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

import FileNameInput from './FileNameInput';
import SectionHelp from './SectionHelp';
import { NoImagesArt } from './EmptySectionArt';
import {
  CARD_SHELL,
  META_BADGE,
  SECTION_HEADING,
  DELETE_BADGE,
} from './fileCardStyles';
import { PANEL_HEADER, PANEL_ICON } from './Panel';
import { V2File } from '../v2Upload.d';

// Six across at the widest: the panel has the full step to itself now, and a
// photo is picked by eye — the name under it is a label, not the thing being
// read, so the tiles can be small.
// Three across leaves the rename field no room on a phone.
const GRID_COLS = 'grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-2.5';

/** Cards fade in 40ms apart so the grid reads as filling rather than as a wall
 * that was always there. Decorative: a card is clickable from its first frame,
 * delay or not. */
const STAGGER_IN =
  'animate-in fade-in duration-base ease-out-strong fill-mode-both motion-reduce:animate-none';

/** Capped at 12 — uncapped, the 24th card would start a full second late, which
 * reads as broken rather than staggered. */
const staggerDelay = (i: number) => `${Math.min(i, 12) * 40}ms`;

function PanelActions({
  takesFiles,
  addLabel,
  count,
  clearLabel,
  onPick,
  onClear,
}: {
  takesFiles: boolean;
  addLabel?: string;
  count: number;
  clearLabel: string;
  onPick: () => void;
  onClear: () => void;
}) {
  return (
    <>
      {takesFiles && (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
          onClick={onPick}
        >
          <Plus className="w-3.5 h-3.5" />
          {addLabel}
        </Button>
      )}
      {count > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 border-destructive-subtle/25 text-destructive-subtle hover:bg-destructive-subtle/10 hover:text-destructive-subtle"
          onClick={onClear}
        >
          <Trash2 className="w-3.5 h-3.5" />
          {clearLabel}
        </Button>
      )}
    </>
  );
}

export function SectionPanel({
  icon: Icon,
  title,
  count,
  badge,
  headerRight,
  help,
  clearLabel,
  onClear,
  addLabel,
  accept,
  onAddFiles,
  onDropFiles,
  children,
}: {
  icon: LucideIcon;
  title: string;
  count: number;
  /** A word about the whole panel, beside its count — "Optional", say. */
  badge?: ReactNode;
  /** Sits against the panel's own buttons, not its title. */
  headerRight?: ReactNode;
  /** What this panel is for, behind a "?" rather than as standing prose. */
  help: string;
  /** Names what the button empties — three buttons all reading `Clear` say
   * nothing about which section is about to lose its files. */
  clearLabel: string;
  onClear: () => void;
  children: ReactNode;
} & Partial<SectionAddProps> & { addLabel?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // Video links are a panel too, and there is no file to add or drop on one.
  const takesFiles = Boolean(onAddFiles && onDropFiles);

  return (
    <div
      onDragOver={(e) => {
        if (!takesFiles) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        onDropFiles?.(e);
      }}
      // h-full + column: paired panels are stretched by their grid row, and
      // without this the border stops at the content and only the wrapper grew.
      className={cn(
        'flex h-full flex-col rounded-xl border bg-card shadow-md overflow-hidden transition-[border-color,box-shadow] duration-fast',
        dragOver && 'border-primary shadow-lg'
      )}
    >
      <div className={cn(PANEL_HEADER, 'flex-wrap gap-x-2.5 gap-y-2')}>
        <Icon className={PANEL_ICON} />
        <span className={SECTION_HEADING}>{title}</span>
        <span className={META_BADGE}>{count}</span>
        {badge}
        <SectionHelp title={title} body={help} />
        <span className="grow" />
        {headerRight}
        <PanelActions
          takesFiles={takesFiles}
          addLabel={addLabel}
          count={count}
          clearLabel={clearLabel}
          onPick={() => input.current?.click()}
          onClear={onClear}
        />
      </div>
      <div className="flex-1 p-3">{children}</div>

      {/* A picked file is still sorted by its extension, so this narrows the
          dialog rather than deciding where the file lands. */}
      {takesFiles && (
        <input
          ref={input}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) onAddFiles?.(e.target.files);
            // eslint-disable-next-line no-param-reassign
            e.target.value = '';
          }}
        />
      )}
    </div>
  );
}

export interface SectionAddProps {
  accept?: string;
  onAddFiles: (files: FileList) => void;
  onDropFiles: (e: React.DragEvent<HTMLDivElement>) => void;
}

interface ImageGridProps extends SectionAddProps {
  images: V2File[];
  thumbnailFileId: string | null;
  onSelect: (fileId: string) => void;
  onClear: () => void;
  onDeleteFile: (fileId: string) => void;
}

export function ImageGrid({
  images,
  thumbnailFileId,
  onSelect,
  onClear,
  onDeleteFile,
  ...add
}: ImageGridProps) {
  return (
    <SectionPanel
      icon={ImageIcon}
      // No descriptive tail on either paired panel: at half width the header
      // wraps to two rows, which costs more than the words are worth. The
      // empty-state caption and the paragraph below both say it anyway.
      title="Images"
      help="Finished shots of the project. Projects with photos get downloaded far more often, and the one you click becomes the card in the feed."
      count={images.length}
      clearLabel="Remove all images"
      onClear={onClear}
      addLabel="Add images"
      {...add}
    >
      {images.length === 0 && <NoImagesArt />}

      {images.length > 0 && (
        <p className="text-sm text-muted-foreground mb-4">
          <strong>Click a photo</strong> to use it as the thumbnail — it shows
          on the project card in the feed.
        </p>
      )}

      <div className={GRID_COLS}>
        {images.map((file, i) => {
          const isSelected = file.id === thumbnailFileId;
          return (
            <div
              key={file.id}
              className={cn(
                CARD_SHELL,
                STAGGER_IN,
                isSelected &&
                  'border-primary shadow-[0_0_0_1px_hsl(var(--primary))]'
              )}
              style={{ animationDelay: staggerDelay(i) }}
            >
              {/* The feed's PostCard draws its thumbnail in a 300x192 box —
                  the same 25:16 here, filled rather than fitted so a photo of
                  any ratio reads as a photo instead of a letterboxed strip. */}
              <button
                type="button"
                onClick={() => onSelect(file.id)}
                title="Use as thumbnail"
                className="relative w-full aspect-[25/16] bg-neutral-50 border-b border-border flex items-center justify-center overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                <img
                  src={file.preview}
                  alt={file.name}
                  className="w-full h-full object-cover"
                />
                {/* On the photo, not under it: which one is the thumbnail is
                    the only thing this grid is asking, so it is answered where
                    the eye already is. */}
                {isSelected && (
                  <span className="absolute left-2 top-2 rounded-full bg-primary px-2 py-0.5 font-tertiary text-[10px] uppercase tracking-wider text-primary-foreground">
                    Thumbnail
                  </span>
                )}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFile(file.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      onDeleteFile(file.id);
                    }
                  }}
                  title="Delete file"
                  aria-label="Delete file"
                  className={DELETE_BADGE}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </span>
              </button>
              {/* Just the name. The size is nobody's decision here, and the
                  extension is already the end of the name. */}
              <div className="p-2">
                <FileNameInput file={file} />
              </div>
            </div>
          );
        })}
      </div>
    </SectionPanel>
  );
}
